import { HttpClient, HttpContext, HttpEvent, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { CaronteSession, init } from '@cyberdefense-im2/cyberdefense-psychopomp-web';
import { from, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ENTRYPOINT, HateoasService, HTTPOPTIONS, IHateoas } from '../../core';
import { CryptoConfig } from '../../core/src/interfaces/config.interface';
import { FullHttpOptions, IHttpOptions, IInitializeParam, ResultObservable } from '../../core/src/interfaces/httpOptions.interface';
import { CharonService } from '../../core/src/services/charon.service';
import { IS_CHARON_REQUEST } from './charon.http.interceptor';
import { CharonSessionService } from './charon.http.session.service';


/**
 * CharonService tem como objetivo facilitar a comunicação das aplicações frontend
 * com as APIs expostas pelo Caronte no padrão `Hateoas`.
 *
 * A interface base é a mesma do `HttpClient` do Angular, então toda a estratégia para realização
 * de requisições usada com ele deve funcionar também neste serviço.
 *
 * Sendo assim o [guia do HttpClient original](https://angular.io/guide/http)
 * é fortemente recomendado no contexto de utilização deste serviço.
 *
 *  * **Module:**
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CharonHttpService extends CharonService {

  initialized = false;
  hasCryptography = false;

  /**
   * @internal
   */
  constructor(
    protected hateoas: HateoasService,
    private http: HttpClient,
    protected charonSession: CharonSessionService,
    @Optional() @Inject(HTTPOPTIONS) private httpOptions: IHttpOptions | undefined,
    @Optional() @Inject(ENTRYPOINT) private entryPoint: string | undefined,
  ) {
    super(hateoas, charonSession);
  }

  /**
   * Método para adicionar configurações default em memória no HateoasService.
   *
   * Method that adds default configuration in memory ate HateoasService.
   *
   * @param options Recebe objeto do tipo IHateoas, com definições de entrypoint e HttpOptions, ambos opcionais
   */
  config(options: IHateoas): void {
    if (this.initialized) {
      this.hateoas.setConfig(options);
      return;
    }
    this.entryPoint = options.entryPoint || this.entryPoint;
    this.httpOptions = {
      ...this.httpOptions,
      ...options.httpOptions,
    };
  }

  /**
   * Método para inicialização do Caronte na aplicação/micro-frontend garantindo o fluxo de requisão no padrão Hateoas.
   *
   * Method for Charon initialization in the application/micro-frontend and ensure the right request flow.
   *
   * @param parameters Define as propriedades necessárias da interface IInitializeParam para inicialização do Caronte
   */
  initialize<ResultData, IHttpOptions>(parameters?: IInitializeParam): ResultObservable<ResultData, IHttpOptions> {
    const localConfig = parameters?.entryPoint || this.entryPoint;
    const body = parameters?.body;
    const baseMethod = parameters?.baseMethod ? parameters.baseMethod : 'GET';
    this.hateoas = this.getHateoasRef(localConfig);
    this.hateoas.setConfig({ httpOptions: this.httpOptions });
    this.initialized = true;

    if (this.hasCryptography) {

      const criptoConfig = this.charonSession.getConfigurations;

      if (!criptoConfig?.initSessionUrl) {
        throw new Error('A URL de troca de chaves do Caronte não foi identificada');
      }

      if (!parameters?.charonID) {
        throw new Error('O parâmetro charonID não foi identificado');
      }

      return this.initSession(parameters, localConfig as string, criptoConfig);
    }

    return this.request(baseMethod, localConfig, { headers: parameters?.headers, params: parameters?.params, body });

  }

  /**
   * Método para criar um request http baseado no entrypoint ou relation
   * inputados seguindo o padrão de comunicação Hateoas.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#request
   *
   * Method to create a http request based on the entrypoint or relation inputed.
   *
   * @internal
   * @param first Tipo do método HTTP
   * @param url URL de requisição
   * @param options Configurações a serem enviadas para a requisição.
   * @returns an observable with the http request created by hateoas service.
   */
  request<ResultData, HttpOptions extends FullHttpOptions>(
    first: string | HttpRequest<any>,
    url?: string,
    options?: IHttpOptions & { body?: any; }): ResultObservable<ResultData, HttpOptions> {
    let req: HttpRequest<any>;

    // ATENÇÃO:
    // O código abaixo é idêntico ao do HttpClient original do Angular, incluindo comentários:

    // First, check whether the primary argument is an instance of `HttpRequest`.
    if (first instanceof HttpRequest) {
      // It is. The other arguments must be undefined (per the signatures) and can be
      // ignored.
      req = first;
    } else {
      // It's a string, so it represents a URL. Construct a request based on it,
      // and incorporate the remaining arguments (assuming `GET` unless a method is
      // provided.
      // Figure out the headers.
      let headers: HttpHeaders | undefined;
      if (!!options?.headers) {
        if (options.headers instanceof HttpHeaders) {
          headers = options.headers;
        } else {
          headers = new HttpHeaders(options.headers);
        }
      }
      headers = this.addAdditionalHeaders(headers);

      // Sort out parameters.
      let params: HttpParams | undefined;
      if (!!options?.params) {
        if (options.params instanceof HttpParams) {
          params = options.params;
        } else {
          params = new HttpParams({ fromObject: options.params });
        }
      }
      params = this.addAdditionalParams(params);
      const httpContext = new HttpContext();
      const context = httpContext.set(IS_CHARON_REQUEST, this.hasCryptography);
      // Construct the request.
      // tslint:disable-next-line:no-non-null-assertion
      req = new HttpRequest(first, url!, (options?.body !== undefined ? options.body : null), {
        headers,
        params,
        context,
        reportProgress: options?.reportProgress,
        // By default, JSON is assumed to be returned for all calls.
        responseType: this.hasCryptography ? 'text' : options?.responseType || 'json',
        withCredentials: options?.withCredentials,
      });
    }

    if (!req.headers.get('x-itau-apikey')) {
      throw new Error('x-itau-apikey não está definido');
    }

    return this.http
      .request(
        req.clone({ url: this.hateoas.getURL(req.url) }),
      )
      .pipe(
        filter((event: HttpEvent<any>) => event instanceof HttpResponse),
        tap(res => this.handleCharonResponse(res as HttpResponse<any>))) as Observable<any>;
  }

  /**
   * Método base para construção de uma requisição HTTP `GET`.
   *
   * A utilização deste método é a mesma do `HttpClient.get` do Angular com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#get
   *
   * Base method to construct a `GET` request.
   *
   * @param url URL da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  get<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    return this.request<ResultData, HttpOptions>('GET', url, options as any);
  };

  /**
   * Método para construção de uma requisição HTTP `POST`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#post
   *
   * Method to construct a `POST` request.
   *
   * @param url URL da requisição.
   * @param body Dados a serem enviados no corpo da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  post<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    body: any | null,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    return this.request<ResultData, HttpOptions>('POST', url, { ...options, body });
  }

  /**
   * Método para construção de uma requisição HTTP `PUT`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#put
   *
   * Method to construct a `PUT` request.
   *
   * @param url URL da requisição.
   * @param body Dados a serem enviados no corpo da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  put<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    body: any | null,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    return this.request<ResultData, HttpOptions>('PUT', url, { ...options, body });
  }

  /**
   * Método para construção de uma requisição HTTP `PATCH`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#patch
   *
   * Method to construct a `PATCH` request.
   *
   * @param url URL da requisição.
   * @param body Dados a serem enviados no corpo da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  patch<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    body: any | null,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    return this.request<ResultData, HttpOptions>('PATCH', url, { ...options, body });
  }


  /**
   * Método para construção de uma requisição HTTP `DELETE`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#delete
   *
   * Method to construct a `DELETE` request.
   *
   * @param url URL da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  delete<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    return this.request<ResultData, HttpOptions>('DELETE', url, options as any);
  }

  /**
   * Método para tratar a resposta do Caronte e persistir seus dados de sessão em memória.
   *
   * Method to Handle Charon response and persist charonSession on memory.
   *
   * @internal
   * @param event Recebe o evento obtido como resposta do API
   */
  private handleCharonResponse(event: HttpResponse<any>) {
    const { headers, body } = event;

    if (body != undefined && body.data != undefined && body.data._links != undefined) {
      this.hateoas.setLinks(body.data._links);
    }

    const charonSessionHeader = headers.get('x-charon-session');
    if (!charonSessionHeader) {
      return;
    }
    this.hateoas.setConfig({
      httpOptions: {
        headers: new HttpHeaders({
          'x-charon-session': charonSessionHeader,
          'X-Content-Encoding': 'application/vnd-encode-4',
        }),
      },
    });
  }

  /**
   * Método que define se a requisição será ou não criptografada.
   *
   * Method that defines whether or not the request will be encrypted.
   *
   * @param hasCrypto flag que indica se a requisição será criptografada
   */
  setCryptography(hasCrypto: boolean): void {
    this.hasCryptography = hasCrypto;
  }

  /**
   * Método que inicializa a sessão no Caronte
   *
   * Method that init session on Caronte
   *
   * @param charonId ID da API cadastrada no Caronte
   * @param localConfig URL de requisição
   * @param headers headers que devem ser passados em tempo de execução
   */
  private initSession(parameters: IInitializeParam, localConfig: string, criptoConfig: CryptoConfig): Observable<any> {
    const headers = parameters.headers;
    if (headers) {
      this.hateoas.setConfig({
        entryPoint: localConfig,
        httpOptions: { headers },
      });
    }
    return from(init({ charonId: parameters.charonID as string, charonBaseUrl: criptoConfig.initSessionUrl }))
      .pipe(
        map((res: CaronteSession) => {
          const headers = new HttpHeaders({
            'X-Charon-Session': res.sessionId as string,
          });

          return { headers, res };
        }),
        switchMap((initResponse) => {
          return this.request('GET', localConfig, { headers: initResponse.headers, responseType: 'text' })
            .pipe(
              map((response: any) => {
                return response.body;
              })
            );
        })
      );
  }

}
